import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Icon from '@material-ui/core/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Fab from '@material-ui/core/Fab';
import Divider from '@material-ui/core/Divider';
import { Field, reduxForm } from 'redux-form';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import ACL_KINDS from 'constants/aclKinds';
import { required, validateCidr, aclItemUnique, maxLength } from 'services/validationMsgs';
import { normalizeCidr } from 'services/normalizer';
import whichIcon from '../WhichIcon';

const propTypes = {
  classes: PropTypes.shape({}).isRequired,
  newAcl: PropTypes.shape({
    id: PropTypes.string,
    cidr: PropTypes.string,
    kind: PropTypes.number,
    description: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  enterCidr: PropTypes.func.isRequired,
  getIp: PropTypes.func.isRequired,
  getIpClear: PropTypes.func.isRequired,
  myip: PropTypes.string,
  enterAclName: PropTypes.func.isRequired,
  selectAclKind: PropTypes.func.isRequired,
  addAcl: PropTypes.func.isRequired,
  newInstanceAcls: PropTypes.instanceOf(Array).isRequired,
  removeAcl: PropTypes.func.isRequired,
  addIpsLater: PropTypes.bool.isRequired,
  checkAddIpsLater: PropTypes.func.isRequired,
  newInstanceService: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  change: PropTypes.func.isRequired,
  touch: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
};

const maxLength128 = maxLength(128);

const defaultProps = {
  myip: null,
};

export function UnstyledStepThree(props) {
  const {
    classes,
    newAcl,
    enterCidr,
    getIp,
    getIpClear,
    myip,
    enterAclName,
    selectAclKind,
    addAcl,
    removeAcl,
    newInstanceAcls,
    addIpsLater,
    checkAddIpsLater,
    newInstanceService,
    change,
    touch,
    reset,
  } = props;

  const hasOpenAcl = newInstanceAcls.filter((acl) => acl.cidr === '0.0.0.0/0').length !== 0;

  const handleAnyIpClick = () => {
    const ipWildcard = '0.0.0.0/0';
    change('ipaddress', ipWildcard);
    touch('ipaddress');
    enterCidr(null, ipWildcard);

    change('name', 'Allow All');
    touch('name');
    enterAclName({ target: { value: 'Allow All' } });
  };

  const handleKindClick = (event, name, kind) => {
    change(name, kind);
    selectAclKind(event, kind);
  };

  const isAclKindRequired = () =>
    (newInstanceService.name.toLowerCase() === 'Elasticsearch'.toLowerCase() ||
      newInstanceService.name.toLowerCase() === 'CockroachDB'.toLowerCase() ||
      newInstanceService.name.toLowerCase() === 'PostgreSQL'.toLowerCase() ||
      newInstanceService.name.toLowerCase() === 'Redis'.toLowerCase() ||
      newInstanceService.name.toLowerCase() === 'TimescaleDB'.toLowerCase()) &&
    typeof newAcl.kind === 'undefined';

  /**
   * Validation function, enables the "Add" button for ACLs as long as
   * 1. The ACL is unique
   * 2. The CIDR is valid
   * 3. The instance service is either ES or Cockroach
   * 4. The ACL being added has a kind (if required for that service)
   * 5. The ACl being added has a name
   *
   * @returns {true || false} - Returns true if all conditions met,
   * false if any one fails
   */
  const isAclValid = () =>
    aclItemUnique(null, null, props) !== undefined ||
    validateCidr(newAcl.cidr) !== undefined ||
    !newAcl.name ||
    isAclKindRequired();

  const aclKindChips = Object.keys(ACL_KINDS)
    .sort()
    .map((key) => {
      const el = ACL_KINDS[key];
      if (ACL_KINDS[key].service === newInstanceService.name && ACL_KINDS[key].label) {
        return (
          <Chip
            key={el.kind}
            color={newAcl.kind === el.kind ? 'primary' : 'default'}
            label={el.label}
            deleteIcon={whichIcon(newAcl.kind === el.kind)}
            onClick={(event) => handleKindClick(event, 'kind', el.kind)}
            onDelete={(event) => handleKindClick(event, 'kind', el.kind)}
            classes={{
              root: classes.chipRoot,
              colorPrimary: classes.chipColorPrimary,
              deleteIcon: classes.chipDeleteIcon,
            }}
          />
        );
      }
      return null;
    });

  const primaryText = (acl) => (
    <Typography variant="body1" className={classes.primary} component="span">
      {`${acl.cidr} ${acl.kind >= 1 ? ACL_KINDS[acl.kind].label : ''}`}
    </Typography>
  );

  const secondaryText = (acl) => (
    <Typography variant="body2" className={classes.secondary} color="textSecondary">
      {`${acl.name}`}
    </Typography>
  );

  const disableTypography = true;

  const aclListItems = newInstanceAcls.map((acl, index) => (
    <React.Fragment key={acl.kind + acl.cidr}>
      <ListItem>
        <ListItemText
          disableTypography={disableTypography}
          primary={secondaryText(acl)}
          secondary={primaryText(acl)}
        />
        <ListItemSecondaryAction>
          <Fab
            size="small"
            classes={{ root: classes.listItemDeleteRoot }}
            onClick={(event) => removeAcl(event, index)}
          >
            <Icon>delete</Icon>
          </Fab>
        </ListItemSecondaryAction>
      </ListItem>
      <Divider />
    </React.Fragment>
  ));

  if (myip) {
    const tempMyip = myip;
    change('ipaddress', myip);
    touch('ipaddress');
    getIpClear(); // Clear the myip variable so we don't execute those statements again.
    enterCidr(null, tempMyip); // Enable the "Add" button.

    change('name', 'My IP');
    touch('name');
    enterAclName({ target: { value: 'My IP' } });
  }

  function handleAnyIpCheck() {
    checkAddIpsLater();
    reset('IP Address');
  }

  return (
    <React.Fragment>
      <Grid container classes={{ container: classes.aclContainer }}>
        <Grid item container xs={12} md={6} classes={{ item: classes.aclFormItem }}>
          <Grid
            item
            container
            xs={12}
            classes={{ item: classes.ipButtonsItem, container: classes.ipButtonsContainer }}
          >
            <Button
              hover-override="green"
              variant="contained"
              classes={{ root: classes.ipButtonRoot }}
              onClick={handleAnyIpClick}
              value="0.0.0.0/0"
            >
              allow any ip
            </Button>
            <Typography align="center" className={classes.or}>
              -OR-
            </Typography>
            <Button
              name="useMyIP"
              hover-override="green"
              variant="contained"
              classes={{ root: classes.ipButtonRoot }}
              onClick={(event) => getIp(event)}
            >
              use my ip
            </Button>
          </Grid>

          <Grid item container classes={{ container: classes.fieldContainer }}>
            <Field
              label="IP Address"
              variant="outlined"
              className={classes.aclFormField}
              name="ipaddress"
              value={newAcl.cidr}
              component={WrappedTextField}
              placeholder="#.#.#.#/#"
              onChange={enterCidr}
              validate={[validateCidr, aclItemUnique, required]}
              normalize={normalizeCidr}
              autoFocus
              InputLabelProps={{
                shrink: true,
                className: classes.inputLabel,
              }}
              disabled={addIpsLater}
            />
            <Field
              label="Name"
              variant="outlined"
              className={classes.aclFormField}
              name="name"
              value={newAcl.name}
              component={WrappedTextField}
              placeholder="Name"
              onChange={enterAclName}
              validate={[maxLength128, required]}
              InputLabelProps={{
                shrink: true,
                className: classes.inputLabel,
              }}
              disabled={addIpsLater}
            />
          </Grid>

          <Typography classes={{ root: classes.chipInstructionsRoot }}>
            Select a Role for the IP:
          </Typography>

          <div className={classes.chipContainer}>{aclKindChips}</div>

          <Field
            name="kind"
            label=""
            component={WrappedTextField}
            className={classes.kindField}
            validate={[aclItemUnique]}
          />

          <Button
            name="submit"
            classes={{ root: classes.addButtonRoot }}
            variant="contained"
            onClick={(event) => addAcl(event, newAcl)}
            disabled={isAclValid()}
            hover-override="magenta"
          >
            <span className={classes.addButtonText}>add</span>
            <Icon classes={{ root: classes.downArrow }}>arrow_downward</Icon>
            <Icon classes={{ root: classes.rightArrow }}>arrow_forward</Icon>
          </Button>
        </Grid>

        <Grid item xs={12} md={6} classes={{ item: classes.aclListItem }}>
          <List>{aclListItems}</List>
          {hasOpenAcl && (
            <Typography className={classes.openAclWarning}>
              0.0.0.0/0 ACLs can be dangerous, as they allow anyone to attempt to log into your
              instance. Please ensure you&apos;re using strong passwords and/or narrow your ACLs to
              trusted ranges.
            </Typography>
          )}
        </Grid>

        <div className={classes.addLater}>
          <FormControlLabel
            classes={{ root: classes.aclLaterLabelRoot }}
            control={<Checkbox />}
            onChange={handleAnyIpCheck}
            checked={addIpsLater}
            label="Add my IPs later"
          />
          <Typography className={classes.aclWarning}>
            Please note that your instance isn&rsquo;t fully setup for access unless an IP is added.
          </Typography>
        </div>
      </Grid>
    </React.Fragment>
  );
}

UnstyledStepThree.propTypes = propTypes;
UnstyledStepThree.defaultProps = defaultProps;

const styles = (theme) => ({
  aclContainer: {
    border: `1px solid ${theme.customColors.black}`,
    padding: theme.spacing(1),
    flexDirection: 'column',

    [theme.breakpoints.up('sm')]: {
      padding: 'unset',
      justifyContent: 'space-between',
      flexDirection: 'row',
      border: 'none',
    },
  },
  aclFormItem: {
    alignSelf: 'flex-start',

    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(1),
      border: `1px solid ${theme.customColors.black}`,
    },
  },
  ipButtonsContainer: {
    flexDirection: 'column',
    marginBottom: 8,

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  },
  ipButtonRoot: {
    color: theme.customColors.white,

    [theme.breakpoints.up('sm')]: {
      flex: '1 0 25%',
    },
  },
  or: {
    margin: '0 16px',
  },
  fieldContainer: {
    flexDirection: 'column',

    '& input': {
      textAlign: 'center',
    },
  },
  aclFormField: {
    marginTop: theme.spacing(1),
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  chipInstructionsRoot: {
    [theme.breakpoints.up('sm')]: {
      margin: '8px auto 0 auto',
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    flexBasis: '100%',
    alignItems: 'center',
    marginTop: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  chipColorPrimary: {
    backgroundColor: theme.customColors.peach,
    color: theme.customColors.black,
  },
  chipRoot: {
    width: '100%',
    justifyContent: 'space-between',
    '&:focus': {
      backgroundColor: theme.customColors.peach,
    },
    '&:not(:last-child)': {
      marginBottom: theme.spacing(1),
    },

    [theme.breakpoints.up('sm')]: {
      '&:not(:last-child)': {
        marginBottom: 'unset',
      },
      width: 'unset',
      margin: '2px 4px',
      flexGrow: 1,
      maxWidth: '210px',
    },
  },
  chipDeleteIcon: {
    color: theme.customColors.teal,
    fontSize: '1.3rem',
  },
  kindField: {
    display: 'none',
  },
  addButtonRoot: {
    backgroundColor: theme.customColors.magenta,
    width: '100%',
    color: theme.customColors.white,
    marginTop: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      width: '50%',
      marginLeft: 'auto',
      marginRight: 'auto',
      marginBottom: theme.spacing(1),
    },

    [theme.breakpoints.up('md')]: {
      width: '125px',
      marginRight: '0',
    },
  },
  addButtonText: {
    [theme.breakpoints.up('md')]: {
      flexGrow: 3,
    },
  },
  aclListItem: {
    [theme.breakpoints.up('md')]: {
      flexBasis: '40%',
    },
  },
  listItemDeleteRoot: {
    color: theme.customColors.white,
    backgroundColor: theme.customColors.red,
  },
  aclLaterLabelRoot: {
    padding: `${theme.spacing(1)}px 0 0 ${theme.spacing(0.5)}px`,
  },
  aclWarning: {
    color: theme.customColors.red,
    fontStyle: 'italic',
    fontSize: '.8rem',
  },
  openAclWarning: {
    color: theme.customColors.red,
    fontStyle: 'italic',
    fontSize: '.8rem',
    marginTop: theme.spacing(1),
    maxWidth: '500px',
  },
  downArrow: {
    fontSize: '1.25rem',

    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  rightArrow: {
    fontSize: '1.25rem',
    display: 'none',

    [theme.breakpoints.up('md')]: {
      display: 'unset',
    },
  },
  rocketContainer: {
    marginTop: theme.spacing(2),
  },
});

export default withStyles(styles)(reduxForm({ form: 'aclEntry' })(UnstyledStepThree));
